/**
 * The handful of things a player is allowed to change.
 *
 * Kept deliberately short. This is a game you open and are already playing;
 * a settings screen with thirty rows in it is a different kind of game. What
 * is here is what genuinely differs between people and devices — how fast the
 * view turns, whether it makes a noise, and how hard the machine is allowed to
 * work — and nothing that is really a preference about someone else's taste.
 *
 * Look sensitivity is stored twice on purpose. A thumb dragging a screen and a
 * mouse on a desk are not the same gesture and never want the same number, and
 * one slider that has to serve both is a slider that is wrong for both.
 */

import type { Tier } from './quality.ts';

export interface Settings {
  sound: boolean;
  /** Radians of yaw per pixel of drag. */
  touchLook: number;
  /** Radians of yaw per pixel of mouse movement. */
  mouseLook: number;
  invertY: boolean;
  /** `auto` lets the device decide and the governor adjust. */
  quality: 'auto' | Tier;
}

export const DEFAULTS: Settings = {
  sound: true,
  touchLook: 0.0034,
  mouseLook: 0.0022,
  invertY: false,
  quality: 'auto',
};

/** The range each slider covers, as [slowest, fastest]. */
export const LOOK_RANGE = {
  touchLook: [0.0014, 0.0075] as const,
  mouseLook: [0.0008, 0.0055] as const,
};

const KEY = 'descent.settings';

export function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const saved = JSON.parse(raw) as Partial<Settings>;
    // Field by field rather than a spread: a stored file from an older build,
    // or one somebody has edited, should not be able to put a string where a
    // number goes and break looking.
    return {
      sound: typeof saved.sound === 'boolean' ? saved.sound : DEFAULTS.sound,
      touchLook: clamp(saved.touchLook, LOOK_RANGE.touchLook, DEFAULTS.touchLook),
      mouseLook: clamp(saved.mouseLook, LOOK_RANGE.mouseLook, DEFAULTS.mouseLook),
      invertY: typeof saved.invertY === 'boolean' ? saved.invertY : DEFAULTS.invertY,
      quality:
        saved.quality === 'mobile' || saved.quality === 'balanced' || saved.quality === 'high' || saved.quality === 'auto'
          ? saved.quality
          : DEFAULTS.quality,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function save(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Private browsing. The settings still apply for this session.
  }
}

function clamp(v: unknown, range: readonly [number, number], fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v)
    ? Math.min(range[1], Math.max(range[0], v))
    : fallback;
}
